import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Badge } from '@jaylabs/ui';
import { API_URL } from '../utils/apiUrl';
import './Friends.css';

function Friends() {
  const navigate = useNavigate();

  // State
  const [friends, setFriends] = useState([]);
  const [pendingSent, setPendingSent] = useState([]);
  const [pendingReceived, setPendingReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [friendCode, setFriendCode] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [generatingCode] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');
  const [toastMsg, setToastMsg] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const storedBg = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';

  const showToast = (type, message) => {
    setToastMsg({ type, message });
    setTimeout(() => setToastMsg(null), 2500);
  };

  const fetchFriends = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (response.ok) {
        setFriends(data.friends);
        setPendingSent(data.pendingSent);
        setPendingReceived(data.pendingReceived);
      } else {
        setError(data.message || 'Failed to fetch friends');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFriendCode = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends/code/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setFriendCode(data.code);
      }
    } catch (err) {
      console.error('Error fetching friend code:', err);
    }
  }, []);

  useEffect(() => {
    fetchFriends();
    fetchFriendCode();
  }, [fetchFriends, fetchFriendCode]);

  const handleSearchEmail = async () => {
    if (!searchEmail.trim()) {
      showToast('error', 'Please enter an email address');
      return;
    }
    try {
      setSearching(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/friends/search?email=${encodeURIComponent(searchEmail)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        setSearchResult(data);
        setInviteStatus('');
      } else {
        showToast('error', data.message || 'Search failed');
      }
    } catch (err) {
      showToast('error', 'Error connecting to server');
    } finally {
      setSearching(false);
    }
  };

  const handleSendInvite = async () => {
    if (!searchEmail.trim()) return;
    setInviteStatus('Sending...');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: searchEmail.trim() })
      });
      const data = await response.json();
      setInviteStatus(response.ok ? (data.message || 'Invitation sent.') : (data.message || 'Failed to send invite.'));
    } catch (err) {
      setInviteStatus('Error sending invite.');
      console.error(err);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ friendUserId: userId })
      });
      const data = await response.json();
      if (response.ok) {
        showToast('success', data.message || 'Friend request sent!');
        setSearchResult(null);
        setSearchEmail('');
        fetchFriends();
      } else {
        showToast('error', data.message || 'Failed to send friend request');
      }
    } catch (err) {
      showToast('error', 'Error connecting to server');
    }
  };

  const handleAcceptFriendRequest = async (friendshipId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends/${friendshipId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        showToast('success', data.message || 'Friend request accepted!');
        fetchFriends();
      } else {
        showToast('error', data.message || 'Failed to accept friend request');
      }
    } catch (err) {
      showToast('error', 'Error connecting to server');
    }
  };

  const handleRemoveFriend = async (friendshipId) => {
    if (confirmRemoveId !== friendshipId) {
      setConfirmRemoveId(friendshipId);
      return;
    }
    setConfirmRemoveId(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends/${friendshipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showToast('info', 'Friend removed');
        fetchFriends();
      } else {
        const data = await response.json();
        showToast('error', data.message || 'Failed to remove friend');
      }
    } catch (err) {
      showToast('error', 'Error connecting to server');
    }
  };

  const handleUseFriendCode = async () => {
    if (!codeInput.trim()) {
      showToast('error', 'Please enter a friend code');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends/code/use`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: codeInput })
      });
      const data = await response.json();
      if (response.ok) {
        showToast('success', data.message || 'Connected!');
        setCodeInput('');
        fetchFriends();
      } else {
        showToast('error', data.message || 'Invalid friend code');
      }
    } catch (err) {
      showToast('error', 'Error connecting to server');
    }
  };

  const kudosMessages = [
    "You're doing amazing! Keep up the great work! 🌟",
    "Your dedication is inspiring! 💪",
    "You're crushing it! So proud of you! 🎉",
    "Keep being awesome! You've got this! 🚀",
    "Your progress is incredible! 🔥",
    "You're a productivity superstar! ⭐",
    "Way to go! Your hard work is paying off! 🎯",
    "You inspire me to do better! 💖",
    "Absolutely crushing those goals! 👏",
    "Your commitment is truly admirable! 🏆"
  ];

  const handleSendKudos = async (friendId, friendName) => {
    const randomMessage = kudosMessages[Math.floor(Math.random() * kudosMessages.length)];
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ recipientId: friendId, message: randomMessage })
      });
      if (response.ok) {
        showToast('success', `Kudos sent to ${friendName}! 💝`);
      } else {
        showToast('error', 'Failed to send kudos');
      }
    } catch (err) {
      showToast('error', 'Error sending kudos');
    }
  };

  return (
    <div
      className="dd-friends-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${storedBg})`
      }}
    >
      <div className="dd-container">
        {/* Header */}
        <div className="dd-hstack dd-mb-8" style={{ justifyContent: 'space-between' }}>
          <h1 className="dd-heading-xl">Friends</h1>
          <Button variant="outline" size="lg" onClick={() => navigate('/dashboard')}>
            Dashboard
          </Button>
        </div>

        {/* My Friends List */}
        {!loading && (
          <div className="dd-card dd-mb-8">
            <h3 className="dd-heading-lg dd-mb-4">My Friends ({friends.length})</h3>

            {friends.length === 0 ? (
              <div className="dd-empty-friends">
                <p className="dd-empty-friends-title">No friends yet</p>
                <p className="dd-empty-friends-text">
                  Use a friend code or search by email to connect with friends!
                </p>
              </div>
            ) : (
              <div className="dd-friends-grid">
                {friends.map((friend) => (
                  <div key={friend.friendshipId} className="dd-friend-card">
                    <div className="dd-vstack dd-gap-3" style={{ alignItems: 'flex-start' }}>
                      <span className="dd-text-semibold dd-text-lg">
                        {friend.displayName || friend.username}
                      </span>
                      <span className="dd-text-sm dd-text-secondary">{friend.email}</span>
                      <div className="dd-hstack dd-gap-2">
                        <Badge variant="primary">Level {friend.level}</Badge>
                        <Badge variant="info">{friend.totalPoints} pts</Badge>
                      </div>
                      <div className="dd-vstack dd-gap-2 dd-full-width">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="dd-full-width"
                          onClick={() => handleSendKudos(friend.userId, friend.displayName || friend.username)}
                        >
                          💝 Send Kudos
                        </Button>
                        {confirmRemoveId === friend.friendshipId ? (
                          <div className="dd-hstack dd-gap-2">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveFriend(friend.friendshipId)}
                            >
                              Confirm
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmRemoveId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="danger"
                            size="sm"
                            className="dd-full-width"
                            onClick={() => handleRemoveFriend(friend.friendshipId)}
                          >
                            Remove Friend
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Friend Code Section */}
        <div className="dd-card dd-mb-8">
          <h3 className="dd-heading-lg dd-mb-4">Your Friend Code</h3>
          <p className="dd-text-sm dd-text-secondary dd-mb-4">
            Share this code with friends to connect quickly
          </p>
          {friendCode ? (
            <div className="dd-hstack dd-gap-3">
              <span className="dd-friend-code-box">{friendCode}</span>
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  navigator.clipboard.writeText(friendCode);
                  showToast('success', 'Friend code copied to clipboard!');
                }}
              >
                Copy
              </Button>
            </div>
          ) : (
            <Button variant="primary" onClick={fetchFriendCode} disabled={generatingCode}>
              Generate Friend Code
            </Button>
          )}
        </div>

        {/* Use Friend Code */}
        <div className="dd-card dd-mb-8">
          <h3 className="dd-heading-lg dd-mb-4">Use a Friend Code</h3>
          <div className="dd-hstack dd-gap-3">
            <Input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="Enter friend code (e.g., NQ-XXXXXXXX)"
              size="lg"
              style={{ fontFamily: 'monospace' }}
            />
            <Button variant="success" size="lg" onClick={handleUseFriendCode}>
              Connect
            </Button>
          </div>
        </div>

        {/* Search by Email */}
        <div className="dd-card dd-mb-8">
          <h3 className="dd-heading-lg dd-mb-4">Find Friends by Email</h3>
          <div className="dd-hstack dd-gap-3 dd-mb-4">
            <Input
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="Enter email address"
              size="lg"
              type="email"
            />
            <Button variant="primary" size="lg" onClick={handleSearchEmail} disabled={searching}>
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {searchResult && (
            <div className="dd-search-result">
              {searchResult.found ? (
                <div className="dd-hstack" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--dd-space-3)' }}>
                  <div className="dd-vstack dd-gap-1" style={{ alignItems: 'flex-start' }}>
                    <span className="dd-text-semibold">
                      {searchResult.user.displayName || searchResult.user.username}
                    </span>
                    <span className="dd-text-sm dd-text-secondary">{searchResult.user.email}</span>
                    <span className="dd-text-xs dd-text-muted">
                      Level {searchResult.user.level} &bull; {searchResult.user.totalPoints} points
                    </span>
                  </div>
                  {searchResult.friendshipStatus === 'none' ? (
                    <Button variant="success" onClick={() => handleSendFriendRequest(searchResult.user.id)}>
                      Send Request
                    </Button>
                  ) : (
                    <Badge variant="info" size="lg">
                      {searchResult.friendshipStatus === 'pending' ? 'Request Pending' : 'Already Friends'}
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="dd-vstack dd-gap-3" style={{ alignItems: 'flex-start' }}>
                  <span className="dd-text-secondary">{searchResult.message}</span>
                  <Button variant="primary" onClick={handleSendInvite}>Send Invitation</Button>
                  {inviteStatus && <span className="dd-text-sm dd-text-muted">{inviteStatus}</span>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pending Friend Requests (Received) */}
        {pendingReceived.length > 0 && (
          <div className="dd-card dd-mb-8">
            <h3 className="dd-heading-lg dd-mb-4">Friend Requests ({pendingReceived.length})</h3>
            <div className="dd-vstack dd-gap-3">
              {pendingReceived.map((request) => (
                <div key={request.friendshipId} className="dd-request-card">
                  <div className="dd-hstack" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--dd-space-3)' }}>
                    <div className="dd-vstack dd-gap-1" style={{ alignItems: 'flex-start' }}>
                      <span className="dd-text-semibold">{request.displayName || request.username}</span>
                      <span className="dd-text-sm dd-text-secondary">{request.email}</span>
                      <span className="dd-text-xs dd-text-muted">
                        Level {request.level} &bull; {request.totalPoints} points
                      </span>
                    </div>
                    <div className="dd-hstack dd-gap-2">
                      <Button variant="success" onClick={() => handleAcceptFriendRequest(request.friendshipId)}>
                        Accept
                      </Button>
                      <Button variant="danger" onClick={() => handleRemoveFriend(request.friendshipId)}>
                        Decline
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Requests (Sent) */}
        {pendingSent.length > 0 && (
          <div className="dd-card dd-mb-8">
            <h3 className="dd-heading-lg dd-mb-4">Pending Requests Sent ({pendingSent.length})</h3>
            <div className="dd-vstack dd-gap-3">
              {pendingSent.map((request) => (
                <div key={request.friendshipId} className="dd-friend-card">
                  <div className="dd-hstack" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--dd-space-3)' }}>
                    <div className="dd-vstack dd-gap-1" style={{ alignItems: 'flex-start' }}>
                      <span className="dd-text-semibold">{request.displayName || request.username}</span>
                      <span className="dd-text-sm dd-text-secondary">{request.email}</span>
                      <Badge variant="info">Waiting for response</Badge>
                    </div>
                    <Button variant="danger" size="sm" onClick={() => handleRemoveFriend(request.friendshipId)}>
                      Cancel Request
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="dd-alert-error dd-mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="dd-text-center dd-py-8">
            <div className="dd-spinner dd-spinner-xl"></div>
            <p className="dd-text-lg dd-text-secondary dd-mt-4">Loading friends...</p>
          </div>
        )}
      </div>

      {toastMsg && (
        <div className={`dd-toast dd-toast-${toastMsg.type}`}>
          {toastMsg.message}
        </div>
      )}
    </div>
  );
}

export default Friends;
